import { useCallback, useEffect, useRef } from 'react';
import BaseBrush, { BaseBrushState, UpdateBrush } from '@visx/brush/lib/BaseBrush';
import { Point } from '@visx/brush/lib/types';
import { pick } from 'lodash';

import { GroupConfig } from '@blockworks/platform/api/research/studio/visualizations';

const initialBrushBoundsByInterval: Record<string, number> = {
    days: 90,
    weeks: 52 * 2,
    months: 12 * 5,
    quarters: Infinity,
    years: Infinity,
};

const useIsTouchDevice = () => {
    if (typeof window === 'undefined') return false;
    // Gesture events are for IOS Safari only - https://caniuse.com/mdn-api_gesturechange_event_touchstart
    const isTouchDevice = 'ongesturestart' in window || 'ontouchstart' in window;
    return isTouchDevice;
};

const getInitialBounds = <T extends {}>(data: T[], interval?: keyof typeof initialBrushBoundsByInterval) => {
    const maxStart = data.length - 1;
    const targetStart = initialBrushBoundsByInterval[interval as keyof typeof initialBrushBoundsByInterval];
    const targetStartIndex = targetStart ? targetStart - 1 : maxStart;
    const end = 0;
    const start = Math.min(targetStartIndex, maxStart);

    return { end, start };
};

const isValidBoundary = (bound: { start: { x?: number }; end: { x?: number } }) => {
    return (bound.start.x ?? 0) < (bound.end.x ?? 0);
};

// Adapt this for refactor to individual
const getGroupYDomain = <T extends {}>(data: T[], group: GroupConfig<T>) => {
    const columns = group.series.map(s => s.column);

    // Removed diverging stacking for now
    // if (group.stack ) {
    //     const yDomain = data.reduce(
    //         (acc, next) => {
    //             const values = Object.values(pick(next,columns)) as number[];
    //             const validValues = values.filter(value => !isNaN(value) && Math.abs(value) !== Infinity);
    //             const positiveMax = sum(validValues.filter(value => value > 0))
    //             const negativeMax = sum(validValues.filter(value => value < 0))

    //             return {
    //                 max: Math.max(acc.max, isNaN(positiveMax) ? 0 : positiveMax),
    //                 min: Math.min(acc.min, isNaN(negativeMax) ? 0 : negativeMax)
    //             };
    //         },
    //         { max: 0, min: 0 },
    //     );
    //     console.log('yDomain',yDomain);
    //     return yDomain;
    // }

    return data.reduce(
        (acc, next) => {
            const values = Object.values(pick(next, columns)) as number[];
            const validValues = values.filter(value => !isNaN(value) && Math.abs(value) !== Infinity);

            const localMax = Math.max(...validValues);
            const localMin = Math.min(...validValues);
            return { min: Math.min(acc.min, localMin), max: Math.max(acc.max, localMax) };
        },
        { max: 0, min: 0 },
    );
};

const getMultipleYDomains = <T extends {}>(data: T[], groups: GroupConfig<T>[]) => {
    return groups
        .map(group => getGroupYDomain(data, group))
        .reduce(
            (acc, next) => {
                return { min: Math.min(acc.min ?? 0, next.min ?? 0), max: Math.max(acc.max ?? 0, next.max ?? 0) };
            },
            { max: 0, min: 0 },
        );
};

const useBrush = ({ isScrollable = false }: { isScrollable?: boolean }) => {
    const brushRef = useRef<BaseBrush>(null);
    const containerRef = useRef<SVGSVGElement>(null);
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !isScrollable) return;
        const scrollHandler = (event: WheelEvent) => {
            if ((event.target as any).nearestViewportElement !== containerRef.current) {
                return false;
            }

            event.preventDefault();
            event.stopPropagation();

            brushRef.current?.updateBrush(prevBrush => {
                const { extent, bounds } = prevBrush;
                const { deltaY } = event;

                const getStart = () => {
                    const x0 = extent.x0 - deltaY / 2;
                    if (x0 < bounds.x0) {
                        return bounds.x0;
                    }
                    if (x0 > extent.x1) {
                        return extent.x1;
                    }
                    return x0;
                };

                const getEnd = () => {
                    const x1 = extent.x1 + deltaY / 2;
                    if (x1 > bounds.x1) {
                        return bounds.x1;
                    }
                    if (x1 < extent.x0) {
                        return extent.x0;
                    }
                    return x1;
                };

                const nextStart = getStart();
                const nextEnd = getEnd();

                const newState: BaseBrushState = {
                    ...prevBrush,
                    start: { y: extent.y0, x: nextStart },
                    end: { y: extent.y1, x: nextEnd },
                    extent: {
                        ...extent,
                        x0: nextStart,
                        x1: nextEnd,
                    },
                };
                const isValid = isValidBoundary(newState);

                if (!isValid) {
                    return prevBrush;
                }

                return newState;
            });
            return true;
        };
        // Typings for this are wonky because of the target element - however this is still a valid event
        container.addEventListener<'wheel'>('wheel', scrollHandler as any);

        return () => {
            container.removeEventListener('wheel', scrollHandler as any);
        };
    }, [isScrollable]);

    const setBrushPosition = useCallback((nextPosition: { start: Partial<Point>; end: Partial<Point> }) => {
        if (brushRef?.current) {
            const updater: UpdateBrush = prevBrush => {
                const newExtent = brushRef.current!.getExtent(nextPosition.start, nextPosition.end);

                const newState: BaseBrushState = {
                    ...prevBrush,
                    start: { y: newExtent.y0, x: newExtent.x0 },
                    end: { y: newExtent.y1, x: newExtent.x1 },
                    extent: newExtent,
                };
                const isValid = isValidBoundary(newState);

                if (!isValid) {
                    return prevBrush;
                }

                return newState;
            };
            brushRef.current.updateBrush(updater);
        }
    }, []);

    return { brushRef, containerRef, setBrushPosition };
};

export {
    getGroupYDomain,
    getInitialBounds,
    getMultipleYDomains,
    initialBrushBoundsByInterval,
    isValidBoundary,
    useBrush,
    useIsTouchDevice,
};
